import { Component, Suspense, lazy, useEffect, useLayoutEffect, useState, type ComponentType, type ReactNode } from 'react';
import { SHARE_PATH, type PublicUser, type Team as TeamInfo } from '@boardflight/shared';
import { api, loadSession, onSessionChange, resumeSession, sessionMayResume, signOut, type Session } from './api';
import { FeaturesProvider } from './features';
import { linkProps, match, navigate, useRoute } from './router';
import { MCP_CALLBACK_PATH } from './oauth-client';
import { NoticeBanner } from './kit/feedback';
import { ConfirmEmailBanner } from './ConfirmEmailBanner';
import { AppBar } from './kit/Shell';
import { PublicFrame } from './kit/public';
import { findShell } from './storefront/link/bridge';
import { LIBRARY_PATH, MINE_PATH, isStorefrontPath } from './storefront/routes';
import { GUIDES_PATH } from './guides';
import { PrerenderedHandoff } from './prerendered';
import { CREATE_HOME, acrossFrom, playHref, surface, surfaceOfPath } from './surface';
import { useAccountFeature } from './account-features';
import { chooseTheme, releaseTheme, useDashboardTheme, useTheme } from './theme';
import { useAsync } from './ui';
import { trackPageView } from './product-analytics';
import { reloadForUpdate } from './update';

/*
 * The pages, each fetched when a route first needs it (`docs/off-board-plan.md` §2.1).
 *
 * Every page used to be imported at the top of this file and so shipped as one script: somebody
 * opening a game's share link on a phone downloaded the playground and every dashboard screen to
 * read one page. Now this module is the route chain and the chrome it draws itself, and the pages
 * arrive in four families:
 *
 *  - the **storefront** — the library and a game's page (`storefront/`), one chunk between them;
 *  - the **dashboard** — a team's screens, a chunk each;
 *  - the **playground**, by far the largest, and the MCP callback that shares its session code;
 *  - the **marketing, guide and legal** pages, and the small signed-out flows.
 *
 * Which chunk a module lands in is the bundler's decision, made from who imports it. So the rule
 * that keeps the split true is this file's and nobody else's: **nothing here imports a page
 * statically.** One `import { X } from './pages/X'` puts X, and everything X reaches, back in the
 * script every visitor downloads — and nothing fails when that happens; a page just gets slower.
 */

/**
 * A page whose script could not be fetched.
 *
 * Nearly always that is a tab loaded from a build that is no longer deployed: BoardFlight ships
 * many times a day, a chunk's name carries its hash, and a file the new build does not have is
 * answered with `index.html`. The page somebody asked for exists — in the build this tab is not
 * running — so the tab reloads itself onto it, through the same door the update notice uses
 * (`update.ts`), which keeps the address and proves it came back on a different build.
 *
 * Once. A reload that did not help — offline, a blocked request — must not become a tab that
 * reloads for ever, so a second failure inside a minute is thrown instead, and `RouteBoundary`
 * says so with a button.
 */
class ChunkError extends Error {}

const CHUNK_RELOADED_AT = 'bf.chunk.reloaded';
const CHUNK_RELOAD_GAP_MS = 60_000;

function chunkFailed(failure: unknown): Promise<never> {
  let lastReload = Number.NaN;
  try {
    lastReload = Number(sessionStorage.getItem(CHUNK_RELOADED_AT) ?? 0);
    // Written before leaving, and the reload only happens if it could be: a tab that cannot
    // remember it has already tried is a tab that would try for ever.
    if (navigator.onLine !== false && Date.now() - lastReload > CHUNK_RELOAD_GAP_MS) {
      sessionStorage.setItem(CHUNK_RELOADED_AT, String(Date.now()));
      reloadForUpdate();
      // Never settles: the page is on its way out, and the fallback is the right thing to show.
      return new Promise<never>(() => undefined);
    }
  } catch {
    // No storage, no automatic reload.
  }
  throw new ChunkError('A page could not be loaded.', { cause: failure });
}

/** `React.lazy` for a page that is a named export, which every page here is. */
function page<P>(load: () => Promise<ComponentType<P>>) {
  return lazy(() => load().then((component) => ({ default: component }), chunkFailed));
}

/*
 * The storefront, behind one lazy component with two doors: a game's short link, and every path
 * under `/library`.
 *
 * One component rather than one per door, and one tree under it: a game opens over the page it was
 * pressed on (`storefront/storefront.tsx`), so stepping from a tile to a game and back has to be a
 * re-render of something that stays mounted — not one page unmounting and another taking its place.
 */
const Library = page(() => import('./storefront/storefront').then((m) => m.Storefront));

// The dashboard.
const Account = page(() => import('./pages/Account').then((m) => m.Account));
const CreatorOnboarding = page(() => import('./pages/CreatorOnboarding').then((m) => m.CreatorOnboarding));
const AssistantPanel = page(() => import('./assistant-panel/AssistantPanel').then((m) => m.AssistantPanel));
const GameDetail = page(() => import('./pages/GameDetail').then((m) => m.GameDetail));
const Games = page(() => import('./pages/Games').then((m) => m.Games));
const NewTeam = page(() => import('./pages/NewTeam').then((m) => m.NewTeam));
const Team = page(() => import('./pages/Team').then((m) => m.Team));

// The playground, and the provider callback that needs its session code and nothing else of it.
const PlaygroundRoute = page(() => import('./pages/PlaygroundRoute').then((m) => m.PlaygroundRoute));
const McpOAuthCallback = page(() => import('./pages/McpOAuthCallback').then((m) => m.McpOAuthCallback));

// Marketing, guides and the two legal documents.
const Agents = page(() => import('./pages/Agents').then((m) => m.Agents));
const Creators = page(() => import('./pages/Creators').then((m) => m.Creators));
const Downloads = page(() => import('./pages/Downloads').then((m) => m.Downloads));
const Get = page(() => import('./pages/Get').then((m) => m.GetRoute));
const Install = page(() => import('./pages/Install').then((m) => m.Install));
const Landing = page(() => import('./pages/Landing').then((m) => m.Landing));
const Privacy = page(() => import('./pages/Legal').then((m) => m.Privacy));
const Start = page(() => import('./pages/Start').then((m) => m.Start));
const Terms = page(() => import('./pages/Legal').then((m) => m.Terms));

// The flows somebody arrives at from an email, a device or a link they were handed.
const ForgotPassword = page(() => import('./pages/Password').then((m) => m.ForgotPassword));
const Invite = page(() => import('./pages/Invite').then((m) => m.Invite));
const Link = page(() => import('./pages/Link').then((m) => m.Link));
const OAuthCallback = page(() => import('./pages/OAuthCallback').then((m) => m.OAuthCallback));
const ResetPassword = page(() => import('./pages/Password').then((m) => m.ResetPassword));
const Say = page(() => import('./pages/Say').then((m) => m.Say));
const SignIn = page(() => import('./pages/SignInStage').then((m) => m.SignIn));
const VerifyEmail = page(() => import('./pages/Password').then((m) => m.VerifyEmail));


/**
 * The `/apps` URL a link from before the rename would carry, as its `/games` equivalent.
 *
 * Only the one segment moves — `/teams/:id/apps/:id/playground` keeps everything around it —
 * and a path that was never an `/apps` path returns null, so nothing else is rewritten. The
 * `apps` segment can only be the first or the third, which is why this is a comparison rather
 * than a replace: a team named `apps` is not a thing, but a game id could be anything.
 */
export function renameAppsPath(path: string): string | null {
  const parts = path.split('/');
  // ['', 'apps', …] and ['', 'teams', ':id', 'apps', …] are the only two shapes.
  const at = parts[1] === 'apps' ? 1 : parts[1] === 'teams' && parts[3] === 'apps' ? 3 : -1;
  if (at === -1) return null;
  parts[at] = 'games';
  return parts.join('/');
}

/**
 * The frame the standalone flows share.
 *
 * They render outside the dashboard chrome — being signed out is the normal case for most of
 * them — so the frame is applied here rather than inside each page, where six copies would
 * drift apart.
 */
function Framed({ signedIn = false, email, children }: { signedIn?: boolean; email?: string; children: React.ReactNode }) {
  return (
    <PublicFrame signedIn={signedIn} email={email}>
      {/* The frame is this file's and is on screen at once; only the flow inside it waits for
          its script. */}
      <div className="bf-centre">
        <Suspense fallback={null}>{children}</Suspense>
      </div>
    </PublicFrame>
  );
}

/** Whether this page is in the desktop app's window, which shows the library and nothing else. */
function inAppWindow(): boolean {
  return typeof window !== 'undefined' && findShell() !== null;
}

/** A path that is only ever another path. Replaced, so Back does not land on the redirect. */
function Go({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

/**
 * What is on screen while a page's script is fetched: the page's own ground and nothing on it.
 *
 * Deliberately not a spinner or a frame. A chunk arrives in the time it takes to notice one, the
 * page that follows draws its own header and its own loading state, and anything shown in between
 * would be a third thing flashing past. The class is the public frame's for its background — the
 * document's own is the old dark one — and the dashboard's ground is the same ivory.
 */
function RouteLoading() {
  return <div className="bf-public" aria-busy="true" />;
}

/**
 * Catches one thing: a page whose script could not be fetched even after the tab reloaded itself
 * (`chunkFailed`). Everything else a render can throw is rethrown, to do whatever it did before
 * this boundary existed — this is here for a failure the split introduced, not as a net under
 * the whole app.
 */
class RouteBoundary extends Component<{ children: ReactNode }, { failure: unknown }> {
  override state: { failure: unknown } = { failure: null };

  static getDerivedStateFromError(failure: unknown) {
    return { failure };
  }

  override render() {
    const { failure } = this.state;
    if (failure === null) return this.props.children;
    if (!(failure instanceof ChunkError)) throw failure;
    return (
      <PublicFrame signedIn={loadSession() !== null}>
        <div className="bf-centre">
          <div role="alert">
            <h1>This page did not load</h1>
            <p>
              BoardFlight may have been updated while this tab was open, or the connection dropped
              on the way. Reloading fetches the page again.
            </p>
            <button type="button" className="primary" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      </PublicFrame>
    );
  }
}

export function App() {
  return (
    <RouteBoundary>
      <Suspense fallback={<RouteLoading />}>
        {/* Ahead of the routes so its layout effect runs first: the static copy of a prerendered
            page goes, and the root it hid is shown, before the page's own layout effects measure
            anything. In this boundary, so it commits only with the routed page. */}
        <PrerenderedHandoff />
        <Routes />
      </Suspense>
    </RouteBoundary>
  );
}

/** The route chain. Order is precedence: the first line that matches is the page. */
function Routes() {
  const path = useRoute();
  const [session, setSession] = useState<Session | null>(() => loadSession());
  /*
   * Signed in on the site's other host, and not yet here (`api.ts`, `resumeSession`). The session
   * is a cookie both hosts share and a stored token only one of them has, so a browser that
   * carries the mark and no stored session is asked once, before anything decides it is a
   * stranger — otherwise a creator arriving from the library would be shown a sign-in page for a
   * moment and then the dashboard. A visitor without the mark never waits.
   *
   * Never in the desktop app's window: its session is the native side's, and the page inside it
   * takes no cookie — asked here because the holder is installed a moment after this first render.
   */
  const [resuming, setResuming] = useState(() => !inAppWindow() && sessionMayResume());

  useEffect(() => onSessionChange(setSession), []);
  // A dark theme drawn ahead of a dashboard nobody turned out to be signed in to (`theme.ts`):
  // taken back before the sign-in page is on screen, which is ivory whatever anybody chose.
  useLayoutEffect(() => {
    if (!resuming && session === null) releaseTheme();
  }, [resuming, session]);
  useEffect(() => {
    if (resuming) void resumeSession().finally(() => setResuming(false));
    // Once, at start: `resuming` is only ever true on the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /*
   * A page view, once the page knows who is reading it: not while a session from the site's other
   * host is being resumed, because the view is the signed-in one that follows, not an anonymous one
   * before it. A page about to be passed to the other host below is not counted either — that host
   * counts it — and `trackPageView` itself is what knows that, so the order of these effects is not
   * what decides it.
   */
  useEffect(() => {
    if (resuming) return;
    let tracked = window.location.hash;
    const track = () => {
      tracked = window.location.hash;
      trackPageView(path, tracked, session !== null);
    };
    // Only a fragment that is not the one counted: going back from a picture's own entry in a phone
    // app (`storefront/link/picture.ts`) lands on the fragment the page was counted at.
    const onHash = () => {
      if (window.location.hash !== tracked) track();
    };
    track();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [path, session?.userId, resuming]);

  /*
   * A page of the site's other host (`surface.ts`): passed on, with its query and fragment, before
   * anything here decides what to draw. It is what keeps an invitation in an inbox or a share link
   * in the dashboard's address bar working — and why nothing below needs to know there are two
   * hosts. Replaced, so Back does not land on an address whose only answer is "not here".
   */
  const here = surface();
  const across = acrossFrom(here, window.location);
  useEffect(() => {
    if (across !== null) window.location.replace(across);
  }, [across]);
  if (across !== null) return <RouteLoading />;

  if (resuming) return <RouteLoading />;

  // Routes someone arrives at from an email or a device, which must render before the shell
  // decides whether anyone is signed in — being signed out is the normal case for all three.
  const resetParams = match('/reset/:token', path);
  if (resetParams) return <Framed><ResetPassword token={resetParams.token!} /></Framed>;

  /*
   * An MCP sign-in coming back from a provider (`McpOAuthCallback`).
   *
   * Above the session check with the other three, and for a stronger reason than theirs: this
   * page has nothing to do with a BoardFlight session at all. What it needs is the *playground*
   * session in this origin's `localStorage`, and it lands in a tab the provider opened, which
   * may not be one that has ever signed in here. Bouncing it to a password box would strand the
   * authorization code on a page nobody comes back to.
   *
   * Matched by prefix rather than by pattern because Codex appends a segment of its own to the
   * callback URL, and how many is its business, not this router's.
   */
  if (path === MCP_CALLBACK_PATH || path.startsWith(`${MCP_CALLBACK_PATH}/`)) {
    return <McpOAuthCallback path={path} />;
  }

  /*
   * A provider sign-in coming back from the API (`OAuthCallback`) — Discord or Google.
   *
   * Above the session check with the others, and for the plainest reason of the lot: the person
   * arriving here is signed out — finishing this is what signs them in. It renders whatever the
   * URL says, including a provider this build has never heard of, because the exchange is what
   * decides that and a 404 from it reads better than a blank page.
   */
  // The frame follows the session, because a new Google or Discord account stays on this page
  // signed in while it answers the new-game emails question, and a header still offering "Sign in"
  // then would read as though the sign-in had not worked. Same element either way, so the page
  // below keeps its state when the frame changes.
  const oauthParams = match('/auth/:provider', path);
  if (oauthParams) {
    return (
      <Framed signedIn={session !== null} email={session?.email}>
        <OAuthCallback provider={oauthParams.provider!} />
      </Framed>
    );
  }

  const verifyParams = match('/verify/:token', path);
  if (verifyParams) return <Framed><VerifyEmail token={verifyParams.token!} /></Framed>;

  if (path === '/forgot') return <Framed><ForgotPassword /></Framed>;

  // Public, and above the session check on purpose: the editor plugin is what someone needs
  // *before* they have an account, because signing in is something the plugin itself does.
  if (path === '/downloads') return <Downloads />;

  // Same reasoning, one step earlier: reading how a game gets onto a Board and publishing a first
  // build both happen before there is an account. Signed-in people can still read them.
  if (path === '/start') return <Start signedIn={session !== null} />;
  if (path === '/install') return <Install signedIn={session !== null} />;
  // `/get` — the desktop app's download, or `/install` again while none is released — and the guides.
  if (path === '/get' || path === '/guides' || path.startsWith('/guides/')) return <Get path={path} signedIn={session !== null} />;
  if (path === '/mcp') return <Agents signedIn={session !== null} />;

  // The front door's two audiences. Above the session check for the same reason as the guides
  // — they are read before there is an account, and by people who will never make one — and
  // above it for one more: a Board owner sent here to read about a store that browses
  // anonymously must not be bounced to a password box. `/players` was the player's page before
  // the front door became it, and it was linkable, so it still answers — with the front door
  // itself, which also makes it the way a signed-in creator, whose `/` is the dashboard, sees
  // what a player sees.
  if (path === '/players') return <Landing signedIn={session !== null} />;
  if (path === '/creators') return <Creators signedIn={session !== null} />;

  // The two documents every account agrees to, read by people who have none yet — the sign-up
  // form links here, and so does the footer of every public page.
  if (path === '/terms') return <Terms signedIn={session !== null} />;
  if (path === '/privacy') return <Privacy signedIn={session !== null} />;

  /*
   * `/say/:key/:build` — the in-game feedback sheet's QR code, landing on a phone.
   *
   * Above the session check, and further above it than anything else here: whoever scanned this
   * is at somebody else's Board with a sentence they want to send, and a
   * password box would end the report exactly as surely as the on-screen keyboard they scanned to
   * escape. A session is used if there is one — the report then says who sent it, unless they
   * choose otherwise — and never asked for (`pages/Say.tsx`).
   */
  const sayParams = match('/say/:key/:build', path);
  if (sayParams) return <Say ingestKey={sayParams.key!} buildId={sayParams.build!} session={session} />;

  /*
   * `/g/:code` — a store game's short link (`docs/store-plan.md` §14).
   *
   * Above the session check with the other public pages, for the plainest reason: it was posted
   * somewhere for strangers to open, and most of them have no account and need none to look. The
   * web Worker has already written the game into this page's `<head>` for whatever crawled it.
   */
  const shareParams = match(`${SHARE_PATH}/:code`, path);
  if (shareParams) return <Library path={path} shareCode={shareParams.code!} signedIn={session !== null} />;

  /*
   * `/library` and everything under it — the storefront (`docs/off-board-plan.md` §2.2).
   *
   * Public for the share link's reason, and it is the same catalog: browsing is anonymous, and a
   * player sent here to look at games must not meet a password box first. One line for the whole
   * of it, because which library page a path means is the storefront's to decide
   * (`storefront/routes.ts`) — a page added there does not move this chain.
   */
  if (isStorefrontPath(path)) return <Library path={path} signedIn={session !== null} />;

  // A redeem link is opened by someone who has never heard of BoardFlight, so the library shows
  // what the code is for and takes an account in place — a dialog over the library, rather than a
  // bounce through sign-in that loses the code on the way (`storefront/redeem/RedeemModal.tsx`).
  const redeemParams = match('/redeem/:code', path);
  if (redeemParams) return <Library path={path} redeem={{ code: redeemParams.code! }} signedIn={session !== null} />;
  if (path === '/redeem') return <Library path={path} redeem={{ code: null }} signedIn={session !== null} />;

  // An invite has to be accepted *as* the invited account, so this one needs a session first.
  const inviteParams = match('/invite/:token', path);
  if (inviteParams && session) return <Framed signedIn email={session.email}><Invite token={inviteParams.token!} /></Framed>;

  if (!session) {
    // The front door is a landing page rather than a password box: somebody typing
    // boardflight.app has come to find out what this is, and a sign-in form answers that
    // with a question. It is the player's page, because the store made players the larger of
    // the two audiences that arrive here; creators get a section of it and `/creators` behind
    // that. Testers on a `/redeem` link never see it. Every other
    // route still lands on sign-in and returns the user to where they were headed.
    //
    // On the creators' host the front door is the dashboard's, and what a stranger meets there is
    // the sign-in in front of it: the page that says what BoardFlight is lives on the other host.
    if (path === '/' && here !== 'create') return <Landing signedIn={false} />;
    // Signing in at the front door lands on the front door, which for an account is the library.
    // `/signup` is the creators' door — it is where their pages send somebody to make an account —
    // so it goes to `/welcome`, which finds a team to start in or the page that makes one. Anywhere
    // else, sign-in returns to where they were headed whichever button they pressed: an invite
    // link must still be accepted, by the new account as much as an old one.
    const front = path === '/signin' || path === '/signup';
    // The desktop app's window takes no password: its session is its native side's, and the
    // sign-in it draws is on My Games (`storefront/link/window-routes.ts`). These three are pages
    // its native side lets it load, so the page turns them down itself.
    if (inAppWindow() && (front || path === '/forgot')) return <Go to={MINE_PATH} />;
    // On a stage of its own (`pages/SignInStage.tsx`), not in the public pages' frame: it is the
    // door, and is drawn as the onboarding is.
    return (
      <SignIn
        // Keyed, so following a link to "Sign in" from `/signup` opens the other form.
        key={path === '/signup' ? 'signup' : 'login'}
        initialMode={path === '/signup' ? 'signup' : 'login'}
        onSignedIn={() => navigate(front ? (path === '/signup' ? '/welcome' : '/') : path)}
      />
    );
  }

  if (path === '/link') return <Framed signedIn email={session.email}><Link /></Framed>;

  // Signed in, BoardFlight opens on the library — for a creator as much as for anybody
  // (`docs/library-first-plan.md` decision 1) — and the root *is* the library, drawn here rather
  // than behind a redirect: the address an account lands on should not have to say `/library`.
  // `/library` is the same page, for everybody. On the dashboard's own host the root is its door.
  if (path === '/') {
    return here === 'create' ? <Go to={CREATE_HOME} /> : <Library path={LIBRARY_PATH} signedIn />;
  }

  return <Dashboard session={session} path={path} />;
}

/** Which team an account was last working in, in this browser. A convenience: see `teamInHand`. */
const LAST_TEAM_KEY = 'boardflight.team';

function recallTeam(userId: string): string | null {
  try {
    const kept = JSON.parse(localStorage.getItem(LAST_TEAM_KEY) ?? 'null') as { userId?: string; teamId?: string } | null;
    return kept?.userId === userId && typeof kept.teamId === 'string' ? kept.teamId : null;
  } catch {
    return null;
  }
}

function rememberTeam(userId: string, teamId: string): void {
  try {
    localStorage.setItem(LAST_TEAM_KEY, JSON.stringify({ userId, teamId }));
  } catch {
    // Storage refused: the bar falls back to the first team after a reload, and that is all.
  }
}

function Dashboard({ session, path }: { session: Session; path: string }) {
  // Every page of the dashboard, the playground among them, in the theme this browser chose.
  useDashboardTheme();
  const theme = useTheme();
  const teams = useAsync(() => api<{ items: TeamInfo[] }>('/v1/teams'), []);
  // Where the admin console lives, asked for only by an admin and told only to one: the console's
  // host is not compiled into this bundle, so nobody else's download contains it.
  const admin = useAsync(
    () => (session.admin ? api<{ user: PublicUser }>('/v1/me') : Promise.resolve(null)),
    [session.admin],
  );
  const adminUrl = admin.data?.user.adminUrl;
  const list = teams.data?.items ?? [];

  /*
   * The team in hand: the one the address names, or — on a page that names none, the account's —
   * the one last worked in. The bar's Games and Team settings lead to it and the account control
   * says its name, so it must not fall back to the first team in the list the moment somebody
   * opens their account from the second. Remembered in this browser, per account, because "the
   * team I was just in" should survive a reload; it is a convenience and nothing depends on it.
   */
  const routeTeamId = (
    match('/teams/:teamId/games/:gameId/playground', path) ??
    match('/teams/:teamId/games/:gameId', path) ??
    match('/teams/:teamId/games', path) ??
    match('/teams/:teamId', path)
  )?.teamId;
  const [lastTeamId, setLastTeamId] = useState<string | null>(() => recallTeam(session.userId));
  useEffect(() => {
    if (!routeTeamId || routeTeamId === 'new' || !list.some((team) => team.id === routeTeamId)) return;
    setLastTeamId(routeTeamId);
    rememberTeam(session.userId, routeTeamId);
  }, [routeTeamId, list, session.userId]);
  const teamInHand = list.find((team) => team.id === routeTeamId) ?? list.find((team) => team.id === lastTeamId) ?? list[0];

  // The dashboard's own front door (`CREATE_HOME`): a team rather than an empty page — the one
  // last worked in, and most accounts have exactly one — and for an account in none, the page that
  // makes one. Replaced, so Back from the games list leaves the dashboard rather than bouncing off
  // this.
  useEffect(() => {
    if (path !== CREATE_HOME || teams.loading || teams.error) return;
    navigate(teamInHand ? `/teams/${teamInHand.id}/games` : '/teams/new', { replace: true });
  }, [path, teamInHand, teams.loading, teams.error]);

  /*
   * `/welcome` — where a signup from the front door lands, email or provider alike.
   *
   * Not a page: a decision, made once the team list is in. An account in a team goes to it; one in
   * none goes to the page that makes one, which on a desktop is where the creator onboarding opens
   * to ask for the team's name. Never `/`, whose answer for a teamless account is the players'
   * front door — right for somebody who redeemed a code, wrong for somebody who just pressed
   * "Create an account" on the creators' page.
   */
  // `/signup` reached with a session already — a bookmark, a second tab — resolves the same way.
  useEffect(() => {
    if ((path !== '/welcome' && path !== '/signup') || teams.loading || teams.error) return;
    navigate(list.length > 0 ? `/teams/${list[0]!.id}/games` : '/teams/new', { replace: true });
  }, [path, list, teams.loading, teams.error]);

  // `/apps` was what these pages were called before games got their own word. Every one of
  // those URLs was linkable — a row in a chat, a bookmark, a CI job's log — so they still
  // resolve, and correct themselves in the address bar rather than 404ing.
  useEffect(() => {
    const renamed = renameAppsPath(path);
    if (renamed) navigate(renamed + window.location.hash, { replace: true });
  }, [path]);

  // A game is a page *of a team*, so its route says so. A bare `/games/:gameId` left the shell
  // with no team at all: the switcher fell back to "Select a team" and the team's own
  // navigation vanished, on the one screen reached by clicking a row in that team's game list.
  // The old path still resolves — see `legacyGameParams` below — because it was linkable.
  const teamGameParams = match('/teams/:teamId/games/:gameId', path);
  const legacyGameParams = match('/games/:gameId', path);
  const teamGamesParams = match('/teams/:teamId/games', path);
  const teamParams = match('/teams/:teamId', path);

  // The playground is a page of a game, and both of the game's paths lead to it — the same
  // pairing `teamGameParams`/`legacyGameParams` have, for the same reason: `/games/:id` was
  // linkable before teams were in the URL and still is.
  const playgroundParams =
    match('/teams/:teamId/games/:gameId/playground', path) ?? match('/games/:gameId/playground', path);

  // `new` is the create form's path segment, and `match('/teams/:teamId', …)` cannot tell it
  // from a team id. The literal therefore wins here and never becomes an `activeTeamId`, which
  // every panel below turns into `/v1/teams/:teamId` requests — a create page answered by four
  // "Not a ULID" error boxes.
  const requestedTeamId =
    playgroundParams?.teamId ?? teamGameParams?.teamId ?? teamGamesParams?.teamId ?? teamParams?.teamId;
  const creatingTeam = requestedTeamId === 'new';
  const activeTeamId = creatingTeam ? undefined : requestedTeamId;
  const activeTeam = list.find((team) => team.id === activeTeamId);

  // The creators' assistant (docs/assistant-plan.md §10): for an account that has it, on the
  // creators' side — never the library, and so never the desktop app's window — and not over the
  // playground, which has its own and owns the keyboard. The panel is pinned to the team in hand
  // and names the game when the page is one.
  const assistantGranted = useAccountFeature('assistant');
  const askHere = assistantGranted && surface() !== 'play' && surfaceOfPath(path) === 'create' && !playgroundParams;
  const [askOpen, setAskOpen] = useState(false);
  const askGameId = teamGameParams?.gameId ?? legacyGameParams?.gameId ?? null;
  const askGame = useAsync(
    () => (askOpen && askGameId ? api<{ name: string }>(`/v1/apps/${askGameId}`) : Promise.resolve(null)),
    [askOpen, askGameId],
  );
  const askTeam = activeTeam ?? teamInHand;

  // Where the creator onboarding may open (`pages/CreatorOnboarding.tsx` decides the rest). A team
  // page, once the list is in — and for an account that makes things: one that is creating a team,
  // or holds any role above tester. Not the playground, which owns the keyboard and is billed by
  // the minute, and not the account page, which a teamless tester reaches to change a password.
  const onboardingHere =
    !teams.loading &&
    !teams.error &&
    !playgroundParams &&
    path !== '/account' &&
    path !== '/welcome' &&
    path !== '/signup' &&
    (creatingTeam || list.some((team) => team.role !== 'tester'));

  function teamCreated(team: TeamInfo) {
    teams.reload();
    navigate(`/teams/${team.id}/games`);
  }

  // Leaving or deleting a team has to land somewhere the user still belongs. The refetch has
  // not arrived yet, so the survivor comes from the list already in hand rather than from a
  // bounce through `/`, whose redirect would still see the team that just went away.
  function teamGone(teamId: string) {
    const next = list.find((team) => team.id !== teamId);
    navigate(next ? `/teams/${next.id}/games` : '/teams/new');
    teams.reload();
  }

  return (
    // Every page below is inside one team, and `/v1/teams` already carries what that team can
    // do — so the whole dashboard is wrapped once here rather than each screen asking again.
    <FeaturesProvider teams={list}>
    <div className="bf-app">
      <AppBar
        teams={list.map((team) => ({ id: team.id, name: team.name, role: team.role }))}
        activeTeamId={teamInHand?.id}
        onSelectTeam={(id) => navigate(`/teams/${id}/games`)}
        onCreateTeam={() => navigate('/teams/new')}
        username={session.username ?? ''}
        email={session.email}
        // Only for an admin, whose own `/v1/me` names the console — so an ordinary account's
        // dashboard has no such element, nothing to notice (§D4), and no address to find.
        adminUrl={adminUrl}
        currentPath={path}
        brandHref={CREATE_HOME}
        guidesHref={playHref(`${GUIDES_PATH}#creators`)}
        libraryHref={playHref(LIBRARY_PATH)}
        onSignOut={() => void signOut().then(() => navigate('/'))}
        linkTo={linkProps}
        theme={theme}
        onTheme={chooseTheme}
        onAsk={askHere ? () => setAskOpen((open) => !open) : undefined}
        askOpen={askHere && askOpen}
      />

      {/* The page failed, the chrome did not: you must still be able to switch team or sign
          out when a request behind the page you asked for did not come back. */}
      {teams.error ? (
        <div className="bf-shell bf-shell-notice">
          <NoticeBanner
            tone="error"
            title="Could not load your teams."
            detail={teams.error}
            actionLabel="Try again"
            onAction={teams.reload}
          />
        </div>
      ) : null}

      <ConfirmEmailBanner />

      {/* The pages arrive a script at a time (see the top of this file), and this is where the
          dashboard waits for one: under the app bar, which stays put, rather than at the root,
          where the whole chrome would blink out for every first visit to a screen. */}
      <Suspense fallback={null}>
      {/* Keyed by the game, because one playground must never be handed the next one's state.
          `useAsync` keeps the last result while it refetches, so without this a jump straight
          from one playground to another — which is what finishing a PixiJS rewrite does — keeps
          the same `Playground` instance mounted and hands it a different game: the old game's
          sandbox session, editor buffers, transcript and open dialogs all carry across. The key
          makes it a remount, which is what a different game is. */}
      {playgroundParams ? (
        <PlaygroundRoute key={playgroundParams.gameId} gameId={playgroundParams.gameId!} teams={list} />
      ) : null}
      {teamGameParams ? (
        <GameDetail gameId={teamGameParams.gameId!} role={activeTeam?.role} />
      ) : null}
      {legacyGameParams ? (
        // The team is not in the URL, so it comes from the game itself, and the address bar
        // is corrected once it arrives — a bookmark or an older link ends up on the same
        // page as a click from the list, with the same team selected.
        <GameDetail
          gameId={legacyGameParams.gameId!}
          onTeamKnown={(teamId) =>
            navigate(`/teams/${teamId}/games/${legacyGameParams.gameId}`, { replace: true })
          }
        />
      ) : null}
      {creatingTeam ? <NewTeam onCreated={teamCreated} /> : null}
      {/* The role decides which of the two game lists this is — the team's catalogue or the
          caller's own entitlements — so it comes from the same team list the switcher uses
          rather than a second request. `undefined` until that list lands. */}
      {teamGamesParams && activeTeamId ? (
        <Games teamId={activeTeamId} teamName={activeTeam?.name ?? ''} role={activeTeam?.role} />
      ) : null}
      {teamParams && activeTeam ? (
        // Keyed by team: the settings form holds an editable draft of the name, and switching
        // teams in the menu must not carry one team's draft onto another.
        <Team
          key={activeTeam.id}
          team={activeTeam}
          userId={session.userId}
          onChanged={teams.reload}
          onGone={() => teamGone(activeTeam.id)}
        />
      ) : null}
      {path === '/account' ? (
        <Account email={session.email} onSignedOut={() => navigate('/')} />
      ) : null}
      </Suspense>

      {/* Last and unconditional, so it stays mounted while step 1's new team reloads the list and
          moves the page underneath it. In a boundary of its own for the same reason: the page
          under it waiting for a script must not hide a dialog somebody is half way through. */}
      <Suspense fallback={null}>
        <CreatorOnboarding
          userId={session.userId}
          eligible={onboardingHere}
          hasTeam={list.length > 0}
          onTeamCreated={teamCreated}
        />
      </Suspense>

      {askHere ? (
        <Suspense fallback={null}>
          <AssistantPanel
            open={askOpen}
            onClose={() => setAskOpen(false)}
            teamId={askTeam?.id ?? null}
            teamName={askTeam?.name ?? null}
            gameName={askGame.data?.name ?? null}
            path={path}
          />
        </Suspense>
      ) : null}
    </div>
    </FeaturesProvider>
  );
}
